import React, { useState, useEffect } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import WebView from "react-native-webview";
import CustomDropdown from "../../../common/general/CustomDropDown/CustomDropdown";
import { Button, RadioButton } from "react-native-paper";
import { DatePickerInput } from "react-native-paper-dates";
import { useForm, Controller } from "react-hook-form";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import ObjectivesWithCriteriaList from "../common/ObjectivesWithCriteriaList";
import ObjectiveForm from "../common/ObjectiveForm";
import VideoPoolManager from "../../../../containers/video/VideoPoolManager";
import LoadingOverlay from "../../../common/status/LoadingOverlay/LoadingOverlay";
import { SparkleBlue } from "../../../svgs/common";
import { PlusCircle } from "../../../svgIcons";
import {
  useGenerateAiTutorFromTitleMutation,
  useGenerateEvalCriteriaMutation,
  useCreateVisualizationFromContentMutation,
  useUpdateVisualizationMutation,
  useGetVisualizationQuery,
  ActivityState,
} from "../../../../graphql/generated/graphql";
import CustomRichTextEditor from "../../../TextEditor/CustomRichTextEditor";

const CONTEXT_CHAR_LIMIT = 250000;

const maybeFormatDueDate = (date) => {
  if (!date) return "";
  if (typeof date === "string" && date.endsWith("Z")) {
    return new Date(date);
  }
  return date;
};

type FormSchema = {
  activityName: string;
  description: string;
  targetAudience: string;
  context: string;
  dueDate: Date | string;
  videoPool: {
    videoId: string;
    objectiveText: string;
    objectiveSequence: number;
    isTeacherAdded: boolean;
  }[];
  activityState: ActivityState | undefined;
  scheduledDate: Date | string | undefined;
};

const InteractiveLearningForm = ({ activityData, onSave, onCancel, isSubmitting }) => {
  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormSchema>({
    defaultValues: {
      activityName: activityData?.title || "",
      description: activityData?.description || "",
      targetAudience: activityData?.targetAudience || "",
      context: activityData?.context ?? "",
      videoPool: activityData?.videoPool || [],
      dueDate: maybeFormatDueDate(activityData?.dueDate) || "",
      activityState: activityData?.activityState || "draft",
      scheduledDate: activityData?.scheduledDate || undefined,
    },
  });

  const checkExistenceOfEvalCriteriaForObjective = (objective) => {
    if (!objective) return false;

    if (
      objective.evalCriteria &&
      objective.evalCriteria.length > 0 &&
      objective.evalCriteria.some((item) => item && item.trim() !== "")
    ) {
      return true;
    }

    return false;
  };

  // State for current step
  const [currentStep, setCurrentStep] = useState(1);
  const [objectives, setObjectives] = useState(activityData?.rawObjectives || []);
  const [hasGeneratedCriteria, setHasGeneratedCriteria] = useState(
    activityData?.rawObjectives?.some((objective) => checkExistenceOfEvalCriteriaForObjective(objective))
  );

  const [videoPool, setVideoPool] = useState(activityData?.videoPool || []);
  const [hasGeneratedDemo, setHasGeneratedDemo] = useState(false);
  const [includeDemo, setIncludeDemo] = useState(true);
  const [visualization, setVisualization] = useState(null);
  const [visualizationId, setVisualizationId] = useState(activityData?.visualizationId || null);
  const [generatingVisualization, setGeneratingVisualization] = useState(false);
  const [visualizationError, setVisualizationError] = useState(null);
  const [newObjective, setNewObjective] = useState("");
  const [newCriteria, setNewCriteria] = useState("");
  const [regeneratingCriteriaForObjective, setRegeneratingCriteriaForObjective] = useState({});
  const [showAddObjectiveForm, setShowAddObjectiveForm] = useState(false);
  const [descriptionEdited, setDescriptionEdited] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [activelyEditing, setActivelyEditing] = useState(false);

  const watchActivityName = watch("activityName");
  const watchDescription = watch("description");
  const watchTargetAudience = watch("targetAudience");
  const watchDueDate = watch("dueDate");
  const watchActivityState = watch("activityState");

  const getVisualizationHtml = () => {
    if (!visualization) return "";

    if (visualization.visualizationType === "interactive") {
      return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <script src="https://cdn.jsdelivr.net/npm/p5@1.4.1/lib/p5.js"></script>
          <style>
            html, body {
              margin: 0;
              padding: 0;
              width: 100%;
              height: 100vh;
              display: flex;
              justify-content: center;
              align-items: center;
              background-color: #FFFFFF;
              overflow: hidden;
            }
            main {
              display: block;
              width: 100%;
              height: 100%;
            }
          </style>
        </head>
        <body>
          <script>
            ${visualization.code}
          </script>
        </body>
      </html>
    `;
    } else if (visualization.visualizationType === "static") {
      return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <script src="https://cdn.jsdelivr.net/npm/mermaid@9.4.3/dist/mermaid.min.js"></script>
          <style>
            html, body {
              margin: 0;
              padding: 0;
              width: 100%;
              height: 100vh;
              display: flex;
              justify-content: center;
              align-items: center;
              background-color: #EAECF0;
              font-family: Arial, sans-serif;
              overflow: hidden;
            }
            .mermaid-container {
              background-color: white;
              border-radius: 8px;
              padding: 16px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              width: 100%;
              max-width: 100%;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
              overflow: auto;
            }
            .mermaid {
              width: 100%;
              overflow: visible;
            }
            #error-message {
              color: #DC2626;
              font-size: 14px;
              text-align: center;
              display: none;
            }
          </style>
        </head>
        <body>
          <div class="mermaid-container">
            <div id="error-message">Error rendering diagram</div>
            <pre class="mermaid">
              ${visualization.code}
            </pre>
          </div>
          <script>
            mermaid.initialize({
              startOnLoad: true,
              theme: 'default',
              securityLevel: 'loose',
              logLevel: 'error',
              flowchart: { htmlLabels: true, useMaxWidth: true },
              er: { useMaxWidth: true },
              sequence: { useMaxWidth: true },
              journey: { useMaxWidth: true }
            });

            mermaid.parseError = function(err, hash) {
              console.error('Mermaid parse error:', err);
              document.getElementById('error-message').style.display = 'block';
            };

            setTimeout(function() {
              try {
                mermaid.init(undefined, document.querySelectorAll('.mermaid'));
              } catch (e) {
                console.error('Mermaid init error:', e);
                document.getElementById('error-message').style.display = 'block';
              }
            }, 500);
          </script>
        </body>
      </html>
    `;
    } else {
      // Default case for other visualizations (like GSAP)
      return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js"></script>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/ScrollTrigger.min.js"></script>
          <style>
            html, body {
              margin: 0;
              padding: 0;
              width: 100%;
              height: 100vh;
              display: flex;
              justify-content: center;
              align-items: center;
              background-color: #EAECF0;
              overflow: hidden;
            }
            #visualization-container {
              background-color: white;
              border-radius: 8px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              width: 100%;
              max-width: 100%;
              height: 100%;
              overflow: hidden;
              display: flex;
              justify-content: center;
              align-items: center;
              position: relative;
            }
          </style>
        </head>
        <body>
          <div id="visualization-container"></div>
          <script>
            try {
              ${visualization.code}
            } catch (e) {
              console.error('Visualization Error:', e);
            }
          </script>
        </body>
      </html>
    `;
    }
  };

  // Generation states
  const [generationState, setGenerationState] = useState({
    isGenerating: false,
    step: null,
    progress: 0,
    isCancellable: true,
  });

  const [evalGenerationState, setEvalGenerationState] = useState({
    isGenerating: false,
    step: null,
    isCancellable: true,
    progress: 0,
  });

  // GraphQL mutations
  const [generateAiTutorFromTitle] = useGenerateAiTutorFromTitleMutation();
  const [generateEvalCriteria] = useGenerateEvalCriteriaMutation();
  const [createVisualizationFromContent] = useCreateVisualizationFromContentMutation();
  const [updateVisualization] = useUpdateVisualizationMutation();
  // const [deleteVisualization] = useDeleteVisualizationMutation();

  // Visualization query
  const {
    data: visualizationData,
    startPolling,
    stopPolling,
    refetch: refetchVisualization,
  } = useGetVisualizationQuery({
    variables: { id: "" },
    fetchPolicy: "network-only",
  });

  // Handler for video pool updates
  const handleVideoPoolUpdate = (updatedVideoPool) => {
    setVideoPool(updatedVideoPool);
    setValue("videoPool", updatedVideoPool);
  };

  type FormattedObjective = {
    objective: string;
    evalCriteria: string[];
    __typename?: string;
  };

  type WindowWithObjectives = Window & {
    __lastGeneratedObjectives?: FormattedObjective[];
  };

  // Effect for visualization data
  useEffect(() => {
    if (visualizationData?.getVisualization?.data?.length > 0) {
      const vis = visualizationData.getVisualization.data[0];
      if (vis.status === "completed") {
        setGeneratingVisualization(false);
        stopPolling();
        setVisualizationId(vis.id);
        setVisualization({
          code: vis.code,
          visualizationType: vis.visualizationType,
          id: vis.id,
        });
        setHasGeneratedDemo(true);
      } else if (vis.status === "failed") {
        setGeneratingVisualization(false);
        stopPolling();
        setVisualizationError("Failed to generate visualization");
      } else {
        setGeneratingVisualization(true);
      }
    }
  }, [visualizationData, stopPolling]);

  useEffect(() => {
    if (visualizationId && !visualization) {
      refetchVisualization({ id: visualizationId });
    }
  }, [visualizationId, refetchVisualization]);

  // Effect for initial load and hiding refresh objectives button
  useEffect(() => {
    if (objectives.length > 0) {
      setIsInitialLoad(true);
      setDescriptionEdited(false);
    } else {
      setIsInitialLoad(false);
      setDescriptionEdited(false);
    }
  }, []);

  useEffect(() => {
    if (watchDescription && objectives.length > 0 && !generationState.isGenerating) {
      setIsInitialLoad(false);
      setDescriptionEdited(true);
    }
  }, [watchDescription]);

  // Handle regenerating evaluation criteria for a specific objective
  const handleRegenerateObjectiveCriteria = async (index, objective) => {
    if (!objective || !objective.objective) return;

    try {
      // Set regenerating state for this specific objective
      setRegeneratingCriteriaForObjective((prev) => ({
        ...prev,
        [index]: true,
      }));

      const result = await generateEvalCriteria({
        variables: {
          input: {
            title: watchActivityName,
            description: watchDescription,
            objectives: [
              {
                objective: objective.objective,
                sequence: index + 1,
              },
            ],
            targetAudience: watchTargetAudience,
          },
        },
      });

      if (result.data?.generateEvalCriteria?.success) {
        const newCriteria = result.data.generateEvalCriteria.data;

        if (newCriteria && newCriteria.length > 0) {
          // Process the criteria for this objective
          let criteriaArray = [];

          if (newCriteria[0].evalCriteria) {
            if (typeof newCriteria[0].evalCriteria === "string") {
              criteriaArray = [newCriteria[0].evalCriteria];
            } else if (Array.isArray(newCriteria[0].evalCriteria)) {
              criteriaArray =
                newCriteria[0].evalCriteria.length > 0
                  ? [newCriteria[0].evalCriteria[0]]
                  : ["Student demonstrates understanding of the objective"];
            }
          } else {
            criteriaArray = ["Student demonstrates understanding of the objective"];
          }

          // Update just this objective's criteria
          const updatedObjectives = [...objectives];
          updatedObjectives[index] = {
            ...updatedObjectives[index],
            evalCriteria: criteriaArray,
          };

          setObjectives(updatedObjectives);
          setHasGeneratedCriteria(true);
        }
      }
    } catch (error) {
      console.error("Error regenerating evaluation criteria:", error);
    } finally {
      // Clear regenerating state for this objective
      setRegeneratingCriteriaForObjective((prev) => ({
        ...prev,
        [index]: false,
      }));
    }
  };

  const handleRemoveObjective = (index) => {
    const newObjectives = [...objectives];
    newObjectives.splice(index, 1);

    // Properly resequence objectives starting from 1, handling both string and object types
    const resequencedObjectives = newObjectives.map((obj, idx) => {
      // Handle both string objectives and object objectives
      if (typeof obj === "string") {
        return {
          id: `objective-${idx}`,
          objective: obj,
          sequence: idx + 1,
          evalCriteria: [],
        };
      } else {
        return {
          ...obj,
          sequence: idx + 1,
          evalCriteria: obj.evalCriteria || [],
        };
      }
    });

    setObjectives(resequencedObjectives);

    if (resequencedObjectives.length === 0) {
      setHasGeneratedCriteria(false);
    }
  };

  const handleEditObjective = (index, updatedObjective) => {
    const newObjectives = [...objectives];
    newObjectives[index] = updatedObjective;
    setObjectives(newObjectives);
  };

  const handleAutoGenerate = async () => {
    if (!watchActivityName) return;

    try {
      setDescriptionEdited(false);
      setGenerationState({
        isGenerating: true,
        step: "Generating activity content",
        progress: 1,
        isCancellable: true,
      });

      const result = await generateAiTutorFromTitle({
        variables: {
          title: watchActivityName,
          targetAudience: watchTargetAudience,
        },
      });

      if (result.data?.generateAiTutorFromTitle?.success) {
        const content = result.data.generateAiTutorFromTitle.data;

        // Update state with generated content
        setValue("description", content.description);

        const structuredObjectives = content.objectives.map((obj, idx) => ({
          id: `objective-${idx}`,
          objective: obj.objective,
          sequence: idx + 1,
          evalCriteria: [],
        }));

        setObjectives(structuredObjectives);

        setGenerationState((prev) => ({
          ...prev,
          step: "Generating evaluation criteria",
          progress: 2,
          isCancellable: false,
        }));

        setEvalGenerationState({
          isGenerating: true,
          step: "Generating evaluation criteria",
          progress: 1,
          isCancellable: false,
        });

        try {
          const evalResult = await generateEvalCriteria({
            variables: {
              input: {
                title: watchActivityName,
                description: content.description,
                objectives: content.objectives.map((obj, idx) => ({
                  objective: obj.objective,
                  sequence: idx + 1,
                })),
                targetAudience: watchTargetAudience,
              },
            },
          });

          if (evalResult.data?.generateEvalCriteria?.success) {
            const newObjectives = evalResult.data.generateEvalCriteria.data;

            // Process the criteria
            const processedObjectives = newObjectives.map((obj, idx) => {
              // Ensure evalCriteria is consistently an array
              let criteriaArray = [];

              if (obj.evalCriteria) {
                if (typeof obj.evalCriteria === "string") {
                  criteriaArray = [obj.evalCriteria];
                } else if (Array.isArray(obj.evalCriteria)) {
                  criteriaArray =
                    obj.evalCriteria.length > 0
                      ? [obj.evalCriteria[0]]
                      : ["Student demonstrates understanding of the objective"];
                }
              } else {
                criteriaArray = ["Student demonstrates understanding of the objective"];
              }

              return {
                id: `objective-${idx}`,
                objective: obj.objective,
                evalCriteria: criteriaArray,
                sequence: idx + 1, // Ensure proper sequencing
              };
            });

            setObjectives(processedObjectives);
            setHasGeneratedCriteria(true);

            try {
              (window as WindowWithObjectives).__lastGeneratedObjectives = processedObjectives.map((obj) => ({
                objective: obj.objective,
                evalCriteria: [...obj.evalCriteria],
                __typename: "FormattedObjective",
              }));
            } catch (e) {
              // Ignore if window assignment fails
            }
          }
        } catch (evalError) {
          console.error("Error generating evaluation criteria:", evalError);
        } finally {
          // Clear evaluation generation state
          setEvalGenerationState({
            isGenerating: false,
            step: null,
            isCancellable: true,
            progress: 0,
          });
        }
      }
    } catch (error) {
      console.error("Error auto-generating content:", error);
    } finally {
      setGenerationState({
        isGenerating: false,
        step: null,
        progress: 0,
        isCancellable: true,
      });
    }
  };

  // Handle generating interactive demo
  const handleGenerateDemo = async () => {
    if (!watchActivityName || !objectives || objectives.length === 0) {
      setVisualizationError("Activity name and objectives are required to generate a visualization");
      return;
    }

    try {
      setGeneratingVisualization(true);
      setVisualizationError(null);

      const result = await createVisualizationFromContent({
        variables: {
          title: watchActivityName,
          description: watchDescription || "",
          objectives: objectives.map((obj, idx) => ({
            objective: typeof obj === "string" ? obj : obj.objective,
            sequence: idx + 1,
          })),
          ...(watchTargetAudience && { context: `Target Audience: ${watchTargetAudience}` }),
        },
      });

      if (!result.data?.createVisualizationFromContent?.success) {
        throw new Error(result.data?.createVisualizationFromContent?.message || "Failed to create visualization");
      }

      const visId = result.data?.createVisualizationFromContent?.data[0].id;

      refetchVisualization({ id: visId });
      startPolling(10000);
    } catch (error) {
      console.error("Error generating visualization:", error);
      setVisualizationError(`Error generating visualization: ${error.message}`);
    }
  };

  const handleGenerateObjectivesAndCriteria = async () => {
    if (!watchActivityName) return;

    try {
      // Store the current description
      const currentDescription = watch("description");

      setDescriptionEdited(false);
      setGenerationState({
        isGenerating: true,
        step: "Generating objectives",
        progress: 1,
        isCancellable: true,
      });

      const result = await generateAiTutorFromTitle({
        variables: {
          title: watchActivityName,
          targetAudience: watchTargetAudience,
        },
      });

      if (result.data?.generateAiTutorFromTitle?.success) {
        const content = result.data.generateAiTutorFromTitle.data;

        const structuredObjectives = content.objectives.map((obj, idx) => ({
          id: `objective-${idx}`,
          objective: obj.objective,
          sequence: idx + 1,
          evalCriteria: [],
        }));

        setObjectives(structuredObjectives);

        setGenerationState((prev) => ({
          ...prev,
          step: "Generating evaluation criteria",
          progress: 2,
          isCancellable: false,
        }));

        // Now set the evaluation generation state to true so loading indicators appear
        setEvalGenerationState({
          isGenerating: true,
          step: "Generating evaluation criteria",
          progress: 1,
          isCancellable: false,
        });

        // Generate evaluation criteria with new objectives but keep original description
        try {
          const evalResult = await generateEvalCriteria({
            variables: {
              input: {
                title: watchActivityName,
                description: currentDescription, // Using original description here
                objectives: content.objectives.map((obj, idx) => ({
                  objective: obj.objective,
                  sequence: idx + 1,
                })),
                targetAudience: watchTargetAudience,
              },
            },
          });

          if (evalResult.data?.generateEvalCriteria?.success) {
            const newObjectives = evalResult.data.generateEvalCriteria.data;

            // Process the criteria
            const processedObjectives = newObjectives.map((obj, idx) => {
              // Ensure evalCriteria is consistently an array
              let criteriaArray = [];

              if (obj.evalCriteria) {
                if (typeof obj.evalCriteria === "string") {
                  criteriaArray = [obj.evalCriteria];
                } else if (Array.isArray(obj.evalCriteria)) {
                  criteriaArray =
                    obj.evalCriteria.length > 0
                      ? [obj.evalCriteria[0]]
                      : ["Student demonstrates understanding of the objective"];
                }
              } else {
                criteriaArray = ["Student demonstrates understanding of the objective"];
              }

              return {
                id: `objective-${idx}`,
                objective: obj.objective,
                evalCriteria: criteriaArray,
                sequence: idx + 1,
              };
            });

            setObjectives(processedObjectives);
            setHasGeneratedCriteria(true);

            // Create global version for cloning
            try {
              (window as WindowWithObjectives).__lastGeneratedObjectives = processedObjectives.map((obj) => ({
                objective: obj.objective,
                evalCriteria: [...obj.evalCriteria],
                __typename: "FormattedObjective",
              }));
            } catch (e) {
              // Ignore if window assignment fails
            }
          }
        } catch (evalError) {
          console.error("Error generating evaluation criteria:", evalError);
        } finally {
          // Clear evaluation generation state
          setEvalGenerationState({
            isGenerating: false,
            step: null,
            isCancellable: true,
            progress: 0,
          });
        }
      }
    } catch (error) {
      console.error("Error auto-generating objectives and criteria:", error);
    } finally {
      setGenerationState({
        isGenerating: false,
        step: null,
        progress: 0,
        isCancellable: true,
      });
    }
  };

  // Handle regenerating interactive demo
  const handleRegenerateDemo = async () => {
    if (!watchActivityName || !objectives || objectives.length === 0) {
      setVisualizationError("Activity name and objectives are required to generate a visualization");
      return;
    }

    try {
      setGeneratingVisualization(true);
      setVisualizationError(null);

      if (visualizationId) {
        const result = await updateVisualization({
          variables: {
            id: visualizationId,
            title: watchActivityName,
            description: watchDescription || "",
            objectives: objectives.map((obj, idx) => ({
              objective: typeof obj === "string" ? obj : obj.objective,
              sequence: idx + 1,
            })),
          },
        });

        if (!result.data?.updateVisualization?.success) {
          throw new Error(result.data?.updateVisualization?.message || "Failed to update visualization");
        }

        const visId = result.data?.updateVisualization?.data[0].id;

        refetchVisualization({ id: visId });
        startPolling(10000);
      } else {
        // If no ID exists, create a new one
        await handleGenerateDemo();
      }
    } catch (error) {
      console.error("Error regenerating visualization:", error);
      setVisualizationError(`Error regenerating visualization: ${error.message}`);
    }
  };

  const onSubmit = (data) => {
    if (!data.activityName.trim()) {
      return;
    }

    setActivelyEditing(false);

    const formattedObjectives = objectives.map((obj, index) => {
      let objective, evalCriteria;

      if (typeof obj === "string") {
        objective = obj;
        evalCriteria = [];
      } else if (typeof obj === "object") {
        objective = obj.objective || obj;
        evalCriteria = Array.isArray(obj.evalCriteria) ? [...obj.evalCriteria] : [];
      } else {
        objective = String(obj);
        evalCriteria = [];
      }

      return {
        objective,
        sequence: index + 1,
        evalCriteria,
      };
    });

    if (formattedObjectives.length === 0) {
      console.error("Cannot create AI Tutor without objectives");
      return;
    }

    // Create the activity data object with all required fields
    const activityData = {
      title: data.activityName,
      description: data.description,
      objectives: formattedObjectives,
      videoUrl: "",
      context: data.context,
      type: "Interactive Learning Experience",
      visualizationId: includeDemo ? visualizationId : null,
      targetAudience: data.targetAudience,
      dueDate: data.dueDate,
      __generatedObjectives: (() => {
        try {
          return (window as WindowWithObjectives).__lastGeneratedObjectives || null;
        } catch (e) {
          return null;
        }
      })(),
      videoPool: videoPool.map((video) => ({
        videoId: video.videoId,
        objectiveText: video.objectiveId || video.objectiveText,
        objectiveSequence: video.objectiveSequence,
        isTeacherAdded: video.isTeacherAdded || false,
      })),
      activityState: data.activityState,
      scheduledDate: data.scheduledDate,
    };

    onSave(activityData);
  };

  // Handle step navigation
  const goToPreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const goToNextStep = () => {
    if (currentStep === 3) {
      setValue("videoPool", videoPool);
    }
    setCurrentStep(currentStep + 1);
  };

  // Render step indicator
  const renderStepIndicator = () => {
    return (
      <View style={styles.stepIndicator}>
        <CustomText text={`Step ${currentStep} of 5`} size="s" useTranslationText={false} />
        <View style={styles.stepProgressBar}>
          {[1, 2, 3, 4, 5].map((step) => (
            <View
              key={step}
              style={[
                styles.stepProgressSegment,
                step <= currentStep ? styles.stepProgressActive : styles.stepProgressInactive,
              ]}
            />
          ))}
        </View>
      </View>
    );
  };

  // Render navigation buttons
  const renderNavButtons = () => {
    const isLastStep = currentStep === 5;
    const isFirstStep = currentStep === 1;

    // Check if any evaluation criteria are empty after generation
    const hasEmptyEvalCriteria = objectives.some(
      (objective) =>
        !objective.evalCriteria ||
        objective.evalCriteria.length === 0 ||
        objective.evalCriteria.some((item) => !item || item.trim() === "")
    );

    // Require valid evaluation criteria if objectives exist
    const requiresEvalCriteria =
      currentStep === 1 && objectives.length > 0 && (!hasGeneratedCriteria || hasEmptyEvalCriteria);

    const isNextDisabled =
      (currentStep === 1 && (!watchActivityName || !watchDescription || objectives.length === 0)) ||
      requiresEvalCriteria;

    return (
      <View style={styles.navButtons}>
        {isFirstStep ? (
          <CustomButton
            text="Cancel"
            onPress={onCancel}
            styleType="primaryLight"
            size="s"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            style={styles.navButton}
          />
        ) : (
          <CustomButton
            text="Back"
            onPress={goToPreviousStep}
            styleType="primaryLight"
            size="s"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            style={styles.navButton}
          />
        )}

        <CustomSpacing type="horizontal" size="s" />

        {!isLastStep ? (
          <CustomButton
            text="Next"
            onPress={goToNextStep}
            styleType={isNextDisabled ? "disabled" : "primary"}
            size="s"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            style={[styles.navButton, isNextDisabled ? { backgroundColor: "#ccc" } : {}]}
            disabled={isNextDisabled}
          />
        ) : (
          <CustomButton
            text={activityData?.id ? "Update Activity" : "Create Activity"}
            onPress={handleSubmit(onSubmit)}
            styleType={isSubmitting ? "disabled" : "primary"}
            size="s"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            style={[styles.navButton, isSubmitting ? { backgroundColor: "#ccc" } : {}]}
            disabled={isSubmitting}
          />
        )}
      </View>
    );
  };

  // Render step 1 content - Basic information
  const renderStep1Content = () => {
    return (
      <View>
        <CustomText text="Let's Start with the Basics" size="l" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="Provide key details about this activity. This will help us generate personalized content for your students."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />

        <CustomSpacing type="vertical" size="l" />
        <CustomText text="Activity name" size="m" weight="ultraBold" useTranslationText={false} />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          key={`activityName-input-${currentStep}`}
          name="activityName"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, onBlur, value } }) => {
            return (
              <CustomTextInput
                placeholder="e.g., Introduction to Velocity"
                onBlur={onBlur}
                onChangeText={(text) => {
                  onChange(text);
                }}
                value={value}
                editable={!generationState.isGenerating}
                style={styles.inputWithButtonField}
              />
            );
          }}
        />
        {errors.activityName && <CustomText text="Activity name is required" style={styles.errorText} />}

        <CustomSpacing type="vertical" size="l" />
        <CustomText text="Target Audience" size="m" weight="ultraBold" useTranslationText={false} />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          name="targetAudience"
          control={control}
          render={({ field: { onChange, value } }) => (
            <CustomDropdown
              placeholder="Select a target audience"
              options={[
                { label: "Elementary School", value: "Elementary School" },
                { label: "Middle School", value: "Middle School" },
                { label: "High School", value: "High School" },
                { label: "Undergraduate", value: "Undergraduate" },
                { label: "Graduate", value: "Graduate" },
              ]}
              value={value}
              onChange={onChange}
              dropdownStyle={styles.dropdown}
            />
          )}
        />
        <CustomSpacing type="vertical" size="s" />
        <CustomButton
          text="Generate Activity Content"
          onPress={handleAutoGenerate}
          loading={generationState.isGenerating}
          disabled={!watchActivityName || generationState.isGenerating}
          styleType="primaryTransparentStroke"
          size="s"
          leftIcon={<SparkleBlue width={16} height={16} />}
          textStyle={{ color: "#3E68FE" }}
        />

        <CustomSpacing type="vertical" size="l" />
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <CustomText text="Activity Description" size="m" weight="ultraBold" useTranslationText={false} />
        </View>
        <CustomSpacing type="vertical" size="m" />
        <View style={styles.editorContainer}>
          <Controller
            name="description"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => {
              const handleDescriptionChange = (content: string) => {
                setActivelyEditing(true);
                onChange(content);
                setValue("description", content, { shouldDirty: true, shouldValidate: false });
                if (objectives.length > 0 && !generationState.isGenerating) {
                  setDescriptionEdited(true);
                }
              };
              return (
                <CustomRichTextEditor
                  value={value}
                  onChange={handleDescriptionChange}
                  isSubmitting={generationState.isGenerating}
                  placeholder="Briefly describe what students will learn in this activity."
                  activelyEditing={activelyEditing}
                />
              );
            }}
          />
        </View>
        {errors.description && <CustomText text="Description is required" style={styles.errorText} />}

        <CustomSpacing type="vertical" size="xl" />

        {!isInitialLoad && descriptionEdited && (
          <>
            <CustomButton
              text="Refresh Objectives"
              onPress={handleGenerateObjectivesAndCriteria}
              loading={generationState.isGenerating}
              disabled={!watchActivityName || generationState.isGenerating}
              styleType="primaryTransparentStroke"
              size="s"
              leftIcon={<SparkleBlue width={16} height={16} />}
              textStyle={{ color: "#3E68FE", fontSize: 14 }}
            />
            <CustomSpacing type="vertical" size="m" />
          </>
        )}
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <CustomText
            text="Learning Objectives and Evaluation Criteria"
            size="m"
            weight="ultraBold"
            useTranslationText={false}
          />
        </View>
        <CustomText
          text="Add Learning Objectives with their Evaluation Criteria to help students understand what they will learn and how they will be evaluated."
          useTranslationText={false}
          style={styles.stepDescription}
        />

        <CustomSpacing type="vertical" size="m" />

        {/* Display existing objectives with criteria using the component */}
        {objectives.length > 0 && (
          <ObjectivesWithCriteriaList
            objectivesWithCriteria={objectives}
            onRemoveObjectiveWithCriteria={handleRemoveObjective}
            onUpdateCriteria={(index, updatedCriteria) => {
              const newObjectives = [...objectives];
              if (newObjectives[index]) {
                newObjectives[index].evalCriteria = updatedCriteria;
                setObjectives(newObjectives);
              }
            }}
            onRegenerateCriteria={handleRegenerateObjectiveCriteria}
            onReorderObjectives={(reorderedObjectives) => {
              setObjectives(reorderedObjectives);
            }}
            onEditObjectiveWithCriteria={handleEditObjective}
            disabled={generationState.isGenerating}
            isGeneratingCriteria={evalGenerationState.isGenerating || generationState.isGenerating}
            isRegeneratingCriteriaForObjective={regeneratingCriteriaForObjective}
          />
        )}

        <CustomSpacing type="vertical" size="m" />

        {/* Add Objective Button */}
        {!showAddObjectiveForm ? (
          <CustomButton
            text="Add Learning Objective"
            onPress={() => setShowAddObjectiveForm(true)}
            styleType="primaryTransparentStroke"
            size="s"
            disabled={generationState.isGenerating}
            leftIcon={<PlusCircle width={16} height={16} />}
            style={{ maxWidth: 220 }}
          />
        ) : (
          <ObjectiveForm
            objective={newObjective}
            criteria={newCriteria}
            onObjectiveChange={setNewObjective}
            onCriteriaChange={setNewCriteria}
            onSave={(data) => {
              const newObj = {
                id: `objective-${objectives.length}`,
                objective: data.objective,
                sequence: objectives.length + 1,
                evalCriteria: data.criteria ? [data.criteria] : [],
              };
              setObjectives([...objectives, newObj]);
              setHasGeneratedCriteria(true);
              setNewObjective("");
              setNewCriteria("");
              setShowAddObjectiveForm(false);
            }}
            onCancel={() => setShowAddObjectiveForm(false)}
            disabled={generationState.isGenerating}
            isEditMode={false}
            saveButtonText="Add Objective with Criteria"
          />
        )}
        <CustomSpacing type="vertical" size="m" />
      </View>
    );
  };

  // Render step 2 content - Contextual Alignment
  const renderStep2Content = () => {
    return (
      <View>
        <CustomText
          text="Now let's add contextual alignment (optional)"
          size="l"
          weight="ultraBold"
          useTranslationText={false}
        />
        <CustomText
          text="Add additional context and enrich the AI's teaching focus for this activity."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />

        <CustomSpacing type="vertical" size="l" />
        <CustomText text="Contextual Alignment" size="m" weight="ultraBold" useTranslationText={false} />

        <CustomSpacing type="vertical" size="s" />
        <View style={styles.infoBox}>
          <CustomText text="What is Contextual Alignment?" size="s" weight="bold" useTranslationText={false} />
          <CustomText
            text="Contextual Alignment allows you to provide specific materials that will guide the AI in creating a personalized learning experience. You can add:"
            size="s"
            useTranslationText={false}
          />
          <View style={styles.bulletList}>
            <CustomText text="• Excerpts from textbooks or academic papers" size="s" useTranslationText={false} />
            <CustomText text="• Specific topics you want the AI to cover" size="s" useTranslationText={false} />
            <CustomText
              text="• Methodologies or teaching approaches to emphasize"
              size="s"
              useTranslationText={false}
            />
            <CustomText text="• Class-specific terminology or concepts" size="s" useTranslationText={false} />
          </View>
          <CustomText
            text="The AI will use this additional context to tailor its teaching approach to align with your specific educational needs and materials."
            size="s"
            useTranslationText={false}
          />
        </View>

        <CustomSpacing type="vertical" size="l" />
        <Controller
          key={`context-input-${currentStep}`}
          name="context"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <CustomTextInput
              placeholder="Add further Contextual Alignment (e.g., paste relevant textbook sections, list specific topics to cover, or describe preferred teaching approaches)"
              onBlur={onBlur}
              onChangeText={onChange}
              value={value}
              numberOfLines={4}
              multiline
              style={styles.multilineInput}
              maxLength={CONTEXT_CHAR_LIMIT}
            />
          )}
        />
        <View style={styles.infoRow}>
          <CustomText
            text={`${watch("context")?.length || 0} / ${CONTEXT_CHAR_LIMIT}`}
            size="xs"
            style={styles.counter}
            useTranslationText={false}
          />
        </View>
      </View>
    );
  };

  // Render step 3 content - Video Recommendations
  const renderStep3Content = () => {
    return (
      <View>
        <CustomText
          text="Add videos to your objectives (optional)"
          size="l"
          weight="ultraBold"
          useTranslationText={false}
        />
        <CustomText
          text="Use the tool below to generate video recommendations based on your learning objectives."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />

        <CustomSpacing type="vertical" size="m" />

        {objectives.length > 0 ? (
          <VideoPoolManager
            title={watchActivityName}
            description={watchDescription}
            objectives={objectives.map((objective, index) => ({
              objective: typeof objective === "string" ? objective : objective.objective,
              sequence: index + 1,
            }))}
            onVideoPoolUpdate={handleVideoPoolUpdate}
            initialVideoPool={videoPool}
          />
        ) : (
          <CustomText
            text="You need to add learning objectives in Step 1 before you can add videos."
            size="m"
            useTranslationText={false}
            style={styles.warningText}
          />
        )}
      </View>
    );
  };

  // Render step 4 content - Interactive Demo
  const renderStep4Content = () => {
    return (
      <View>
        <View style={{ flexDirection: "row" }}>
          <CustomText text="Enhance with an Interactive Demo " size="l" weight="ultraBold" useTranslationText={false} />
          <CustomText
            text="(Experimental)"
            size="l"
            weight="ultraBold"
            useTranslationText={false}
            style={{ color: "red" }}
          />
        </View>
        <CustomText
          text="Generate an interactive demo to reinforce key concepts. Preview it below and decide if you'd like to include it in the activity. This is an experimental feature that may not always work but try it out!."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />

        <CustomSpacing type="vertical" size="m" />

        {!hasGeneratedDemo ? (
          <CustomButton
            text="Generate Interactive Demo"
            onPress={handleGenerateDemo}
            loading={generatingVisualization}
            disabled={generatingVisualization || !objectives || objectives.length === 0}
            styleType="primary"
            size="m"
          />
        ) : (
          <View>
            {visualization && (
              <View style={styles.demoPreviewContainer}>
                <WebView
                  source={{ html: getVisualizationHtml() }}
                  style={styles.webView}
                  scrollEnabled={false}
                  javaScriptEnabled={true}
                  domStorageEnabled={true}
                  allowFileAccess={false}
                  allowUniversalAccessFromFileURLs={false}
                  originWhitelist={["*"]}
                  startInLoadingState={true}
                  onError={(syntheticEvent) => {
                    const { nativeEvent } = syntheticEvent;
                    console.error("WebView error:", nativeEvent);
                  }}
                />
              </View>
            )}

            <CustomSpacing type="vertical" size="m" />
            <CustomText text="Include this demo in the activity?" size="m" weight="bold" useTranslationText={false} />
            <View style={styles.radioButtonsContainer}>
              <View style={styles.radioOption}>
                <RadioButton
                  value="yes"
                  status={includeDemo ? "checked" : "unchecked"}
                  onPress={() => setIncludeDemo(true)}
                  color="#3E68FE"
                />
                <CustomText text="Yes, include the interactive demo" size="s" useTranslationText={false} />
              </View>
              <View style={styles.radioOption}>
                <RadioButton
                  value="no"
                  status={!includeDemo ? "checked" : "unchecked"}
                  onPress={() => setIncludeDemo(false)}
                  color="#3E68FE"
                />
                <CustomText text="No, don't include it" size="s" useTranslationText={false} />
              </View>
            </View>

            <CustomSpacing type="vertical" size="m" />
            <CustomButton
              text="Re-Generate Interactive Demo"
              onPress={handleRegenerateDemo}
              loading={generatingVisualization}
              disabled={generatingVisualization}
              styleType="primaryLight"
              size="s"
            />
          </View>
        )}

        {visualizationError && (
          <CustomText text={visualizationError} style={styles.errorText} size="s" useTranslationText={false} />
        )}
      </View>
    );
  };

  // Render step 5 content - Review
  const renderStep5Content = () => {
    const objectivesWithVideos = new Set();
    videoPool.forEach((video) => {
      if (video.objectiveSequence) {
        objectivesWithVideos.add(video.objectiveSequence);
      } else if (video.objectiveText) {
        const objective = objectives.findIndex(
          (obj) =>
            (typeof obj === "string" ? obj : obj.objective) === video.objectiveText ||
            (typeof obj === "object" && obj.objective === video.objectiveId)
        );
        if (objective !== -1) {
          objectivesWithVideos.add(objective + 1);
        }
      }
    });

    return (
      <View>
        <CustomText text="Review your activity" size="l" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="Review the details of your activity before creating it."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />

        <CustomSpacing type="vertical" size="m" />

        <View style={styles.reviewSection}>
          <CustomText text="Activity name" size="m" weight="ultraBold" useTranslationText={false} />
          <CustomText text={watchActivityName} size="m" useTranslationText={false} style={styles.reviewText} />
        </View>

        <CustomSpacing type="vertical" size="m" />

        <View style={styles.reviewSection}>
          <CustomText text="Activity Description" size="m" weight="ultraBold" useTranslationText={false} />
          <View style={styles.reviewTextContainer}>
            <CustomText
              text={watchDescription ? watchDescription.replace(/<[^>]*>/g, "") : ""}
              size="m"
              useTranslationText={false}
              style={styles.reviewText}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="m" />

        {/* Add Due Date Section */}
        <View style={styles.reviewSection}>
          <CustomText text="Due Date (Optional)" size="m" weight="ultraBold" useTranslationText={false} />
          <CustomSpacing type="vertical" size="s" />
          <Controller
            name="dueDate"
            control={control}
            rules={{ required: false }}
            render={({ field: { onChange, value } }) => (
              <DatePickerInput
                locale="en"
                value={value instanceof Date ? value : value ? new Date(value) : undefined}
                onChange={onChange}
                inputMode="start"
                mode="outlined"
                outlineColor={"#D1D5DB"}
                selectionColor={false}
                activeOutlineColor={"blue"}
                withDateFormatInLabel={false}
                placeholder="MM/DD/YYYY"
                inputProps={{
                  color: "#000000",
                  mode: "outline",
                }}
                theme={{
                  colors: "#000000",
                  roundness: 8,
                  opacity: 1,
                }}
                style={styles.datePicker}
                aria-label="Due Date date picker"
              />
            )}
          />
          {watchDueDate && (
            <CustomText
              text={`Due: ${new Date(watchDueDate).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}`}
              size="s"
              useTranslationText={false}
              style={[styles.reviewText, { marginTop: 8, color: "#3E68FE" }]}
            />
          )}
        </View>

        <CustomSpacing type="vertical" size="m" />

        <View style={styles.reviewSection}>
          <CustomText text="Learning Objectives" size="m" weight="ultraBold" useTranslationText={false} />
          {objectives.map((objective, index) => (
            <View key={index} style={styles.objectiveReviewItem}>
              <CustomText
                text={typeof objective === "string" ? objective : objective.objective}
                size="m"
                useTranslationText={false}
                style={styles.reviewText}
              />
              {objectivesWithVideos.has(index + 1) && (
                <CustomText text="Video" size="xs" useTranslationText={false} style={styles.videoTag} />
              )}
            </View>
          ))}
        </View>

        {hasGeneratedDemo && includeDemo && (
          <View style={styles.reviewSection}>
            <CustomText
              text="Interactive Demo Included"
              size="m"
              useTranslationText={false}
              style={styles.reviewText}
              weight="ultraBold"
            />
          </View>
        )}

        {watch("context") && watch("context").trim() !== "" && (
          <>
            <CustomSpacing type="vertical" size="m" />
            <View style={styles.reviewSection}>
              <CustomText text="Contextual Alignment" size="m" weight="ultraBold" useTranslationText={false} />
              <CustomText text={watch("context")} size="m" useTranslationText={false} style={styles.reviewText} />
            </View>
          </>
        )}

        <CustomSpacing type="vertical" size="m" />
        <View style={styles.activityState}>
          <CustomText text="Publish this Activity" size="l" weight="ultraBold" useTranslationText={false} />
          <CustomText
            text="Publish this activity now or schedule a publish date."
            size="s"
            useTranslationText={false}
            style={styles.stepDescription}
          />
          <Controller
            control={control}
            name="activityState"
            render={({ field: { onChange, value } }) => (
              <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
                {Object.keys(ActivityState).map((state) => (
                  <CustomButton
                    key={state}
                    text={ActivityState[state]}
                    size="s"
                    style={[styles.activityStateOption, ActivityState[state] === value && styles.activeState]}
                    onPress={() => {
                      onChange(ActivityState[state]);
                    }}
                  />
                ))}
              </div>
            )}
          />
          {watchActivityState === ActivityState.Scheduled && (
            <>
              <CustomText
                text="This Activity will automatically be published after:"
                size="s"
                useTranslationText={false}
                style={styles.stepDescription}
              />
              <Controller
                control={control}
                name="scheduledDate"
                render={({ field: { onChange, value } }) => (
                  <div style={{ maxWidth: 250, marginTop: 10 }}>
                    <DatePickerInput
                      locale="en"
                      value={value instanceof Date ? value : value ? new Date(value) : undefined}
                      onChange={onChange}
                      inputMode="start"
                      mode="outlined"
                      outlineColor={"#D1D5DB"}
                      selectionColor={undefined}
                      activeOutlineColor={"blue"}
                      withDateFormatInLabel={false}
                      placeholder="MM/DD/YYYY"
                      theme={{
                        colors: {
                          primary: "#000000",
                          onSurface: "#667085",
                        },
                        roundness: 8,
                      }}
                      style={styles.datePicker}
                      aria-label="Schedule published date picker"
                    />
                  </div>
                )}
              />
            </>
          )}
        </View>
      </View>
    );
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1Content();
      case 2:
        return renderStep2Content();
      case 3:
        return renderStep3Content();
      case 4:
        return renderStep4Content();
      case 5:
        return renderStep5Content();
      default:
        return renderStep1Content();
    }
  };

  // Configure loading states for loading overlays
  const loadingConfig = {
    visible: generationState.isGenerating || evalGenerationState.isGenerating || generatingVisualization,
    step: generationState.isGenerating
      ? generationState.step
      : evalGenerationState.isGenerating
        ? evalGenerationState.step
        : "Generating interactive demo",
    progress: generationState.isGenerating
      ? generationState.progress
      : evalGenerationState.isGenerating
        ? evalGenerationState.progress
        : 1,
    message: generationState.isGenerating
      ? "Generating activity content..."
      : evalGenerationState.isGenerating
        ? "Generating evaluation criteria..."
        : "Creating an interactive visualization based on your content...",
    isCancellable: generationState.isGenerating
      ? generationState.isCancellable
      : evalGenerationState.isGenerating
        ? evalGenerationState.isCancellable
        : true,
    onCancel: generationState.isGenerating
      ? () => setGenerationState({ ...generationState, isGenerating: false })
      : evalGenerationState.isGenerating
        ? () => setEvalGenerationState({ ...evalGenerationState, isGenerating: false })
        : () => setGeneratingVisualization(false),
  };

  return (
    <View style={styles.container} id="interactive-learning-form-container">
      <View style={styles.formContainer}>
        <View style={styles.header}>
          <CustomText
            text={`${activityData?.id ? "Edit" : "Create"} Interactive Learning Experience`}
            size="xl"
            weight="bold"
            style={{ padding: 32 }}
            useTranslationText={false}
          />
        </View>

        <View style={styles.stepsContainer}>{renderStepIndicator()}</View>

        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={true}
          tabIndex={0}>
          <View style={styles.content}>
            {renderStepContent()}

            <View style={styles.spacer} />

            {renderNavButtons()}
          </View>
        </ScrollView>

        {/* Loading Overlay */}
        {loadingConfig.visible && (
          <LoadingOverlay
            visible={loadingConfig.visible}
            step={loadingConfig.step}
            progress={loadingConfig.progress}
            message={loadingConfig.message}
            isCancellable={loadingConfig.isCancellable}
            onCancel={loadingConfig.onCancel}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  formContainer: {
    flex: 1,
    padding: 0,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 32,
  },
  stepIndicator: {
    marginBottom: 20,
  },
  stepProgressBar: {
    flexDirection: "row",
    marginTop: 10,
  },
  stepProgressSegment: {
    flex: 1,
    height: 4,
    marginHorizontal: 2,
    borderRadius: 2,
  },
  stepProgressActive: {
    backgroundColor: "#3E68FE",
  },
  stepProgressInactive: {
    backgroundColor: "#E6E6E6",
  },
  stepDescription: {
    color: "#667085",
    marginTop: 5,
  },
  inputWithButtonField: {
    marginBottom: 10,
  },
  dropdown: {
    borderColor: "#D1D5DB",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
  },
  editorContainer: {
    marginBottom: 20,
  },
  multilineInput: {
    height: 150,
    textAlignVertical: "top",
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  counter: {
    color: "#667085",
  },
  warningText: {
    color: "#DC2626",
  },
  demoPreviewContainer: {
    width: "100%",
    height: 400,
    backgroundColor: "#F2F4F7",
    borderRadius: 8,
    overflow: "hidden",
    marginBottom: 20,
  },
  webView: {
    flex: 1,
    width: "100%",
    height: "100%",
  },
  radioButtonsContainer: {
    marginTop: 10,
  },
  radioOption: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 5,
  },
  reviewSection: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  reviewTextContainer: {
    marginTop: 10,
  },
  reviewText: {
    marginTop: 5,
  },
  objectiveReviewItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 10,
    padding: 5,
    backgroundColor: "#F2F4F7",
    borderRadius: 4,
  },
  videoTag: {
    backgroundColor: "#E6EBFF",
    color: "#3E68FE",
    padding: 4,
    borderRadius: 4,
  },
  spacer: {
    flex: 1,
  },
  navButtons: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 24,
    marginBottom: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  navButton: {
    minWidth: 100,
  },
  errorText: {
    color: "#FF0000",
    marginTop: 8,
  },
  header: {
    backgroundColor: "#F2F4F7",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    padding: 0,
    width: "100%",
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
  },
  stepsContainer: {
    paddingHorizontal: 32,
    paddingVertical: 16,
  },
  infoBox: {
    backgroundColor: "#f5f7ff",
    borderRadius: 8,
    padding: 16,
  },
  bulletList: {
    marginVertical: 8,
    paddingLeft: 8,
  },
  datePicker: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D5D7DA",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
  },
  activityState: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  activeState: {
    backgroundColor: "#0084FF1A",
    color: "white",
    borderColor: "#0084FF",
  },
  activityStateOption: {
    borderRadius: 30,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 2,
    borderStyle: "solid",
    color: "#667085",
    borderColor: "transparent",
  },
});

export default InteractiveLearningForm;
